/*
 * <video-autoplay>
 * Wraps a muted, looping background <video> (Shopify-hosted) or a provider
 * <iframe> (YouTube/Vimeo) for the "Video banner (autoplay)" section.
 *
 * The media never loads until this element decides it should:
 *  - <source>/<iframe> URLs are held in `data-src` and only promoted to `src`
 *    when motion is allowed, so nothing is fetched under reduced motion.
 *  - When `prefers-reduced-motion: reduce` is set, the video is never
 *    initialized and the section's cover image remains the only visual.
 */
if (!customElements.get('video-autoplay')) {
  customElements.define(
    'video-autoplay',
    class VideoAutoplay extends HTMLElement {
      connectedCallback() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.media = this.closest('.video-banner__media');
        const video = this.querySelector('video');

        if (video) {
          this.playVideo(video);
        } else {
          this.playIframe(this.querySelector('iframe'));
        }
      }

      playVideo(video) {
        const sources = video.querySelectorAll('source[data-src]');
        if (!sources.length) return;

        sources.forEach((source) => {
          source.src = source.dataset.src;
        });

        video.muted = true;
        video.addEventListener('playing', () => this.revealMedia(), { once: true });
        video.addEventListener('error', () => this.hideMedia(video), { once: true });
        video.load();

        const playback = video.play();
        if (playback !== undefined) playback.catch(() => {});
      }

      playIframe(iframe) {
        if (!iframe || !iframe.dataset.src) return;

        iframe.addEventListener('load', () => this.revealMedia(), { once: true });
        iframe.src = iframe.dataset.src;
      }

      revealMedia() {
        if (this.media) this.media.classList.add('video-banner__media--playing');
      }

      hideMedia(video) {
        // The video could not load — fall back to the cover image.
        if (this.media) this.media.classList.remove('video-banner__media--playing');
        video.hidden = true;
      }
    }
  );
}
